// contracts/contracts.service.ts
import { api } from './api';
import type { ApiResponse } from '../utils/types';
import { toast } from 'sonner';

// Types
export interface PaymentMilestone {
    id?: string;
    name: string;
    percentage: number;
    dueDate?: string;
    status: 'pending' | 'invoiced' | 'paid';
    paidAt?: string;
    invoiceId?: string;
}

export interface PaymentTerms {
    type: 'upfront' | 'milestone' | 'completion' | 'net30' | 'net60';
    milestones: PaymentMilestone[];
}

export interface Deliverable {
    id?: string;
    title: string;
    description?: string;
    deadline?: string;
    status: 'pending' | 'in_progress' | 'completed' | 'approved';
    completedAt?: string;
}

export interface Compliance {
    ftcDisclosure: boolean;
    brandApproval: boolean;
    usageRightsYears: number;
    exclusivityDays: number;
    revisionsAllowed: number;
    killFeePercentage: number;
}

export interface Signatures {
    creatorSignedAt?: string;
    creatorSignature?: string;
    brandSignedAt?: string;
    brandSignature?: string;
}

export interface Amendment {
    id?: string;
    version: number;
    changes: string;
    amendedAt: string;
    amendedBy: string;
}

export interface ContractFlag {
    id?: string;
    type: 'payment' | 'delivery' | 'compliance' | 'communication' | 'other';
    description: string;
    createdAt: string;
    resolvedAt?: string;
    resolution?: string;
}

export interface ActivityLog {
    id?: string;
    action: 'created' | 'status_change' | 'signed' | 'deliverable_updated' | 'flag_added' | 'flag_resolved' | 'amended' | 'sent' | 'viewed';
    description: string;
    metadata?: Record<string, any>;
    performedBy?: string;
    timestamp: string;
}

export interface Contract {
    id: string;
    partnershipId: string;
    partnership?: {
        id: string;
        brand: string;
        contact: string;
        email: string;
        value?: number;
        contentType?: string;
    };
    teamId: string;
    title: string;
    status: 'draft' | 'sent' | 'negotiation' | 'signed' | 'active' | 'completed' | 'terminated';
    totalValue: number;
    startDate: string;
    endDate: string;
    paymentTerms?: PaymentTerms;
    deliverables: Deliverable[];
    compliance?: Compliance;
    signatures?: Signatures;
    contractFileUrl?: string;
    autoRenewal: boolean;
    terminationNoticeDays: number;
    amendments: Amendment[];
    flags: ContractFlag[];
    activityLog?: ActivityLog[];
    notes?: string;
    tags: string[];
    createdBy?: string;
    createdAt: string;
    updatedAt: string;
}

export interface ContractTemplate {
    id: string;
    teamId: string;
    name: string;
    description?: string;
    category: 'sponsorship' | 'affiliate' | 'collaboration' | 'licensing' | 'other';
    defaultTerms?: {
        paymentTerms?: {
            type: string;
            defaultMilestones: { name: string; percentage: number }[];
        };
        compliance?: Partial<Compliance>;
        terminationNoticeDays?: number;
        autoRenewal?: boolean;
        defaultDeliverables?: string[];
    };
    templateFileUrl?: string;
    isActive: boolean;
    createdBy?: string;
    createdAt: string;
    updatedAt: string;
}

export interface ContractStats {
    total: number;
    totalValue: number;
    byStatus: Record<string, number>;
    activeContracts: number;
    pendingSignature: number;
    expiringThisMonth: number;
    completedValue: number;
    pendingValue: number;
}

export interface CreateContractData {
    partnershipId: string;
    title: string;
    totalValue: number;
    startDate: string;
    endDate: string;
    paymentTerms?: Partial<PaymentTerms>;
    deliverables?: Partial<Deliverable>[];
    compliance?: Partial<Compliance>;
    autoRenewal?: boolean;
    terminationNoticeDays?: number;
    notes?: string;
    tags?: string[];
}

export interface UpdateContractData extends Partial<CreateContractData> {
    contractFileUrl?: string;
}

// API endpoints
const API_ENDPOINTS = {
    contracts: '/contracts',
    contract: (id: string) => `/contracts/${id}`,
    status: (id: string) => `/contracts/${id}/status`,
    signCreator: (id: string) => `/contracts/${id}/sign/creator`,
    signBrand: (id: string) => `/contracts/${id}/sign/brand`,
    deliverables: (id: string) => `/contracts/${id}/deliverables`,
    deliverable: (id: string, delId: string) => `/contracts/${id}/deliverables/${delId}`,
    amendments: (id: string) => `/contracts/${id}/amendments`,
    flags: (id: string) => `/contracts/${id}/flags`,
    resolveFlag: (id: string, flagId: string) => `/contracts/${id}/flags/${flagId}/resolve`,
    upload: (id: string) => `/contracts/${id}/upload`,
    stats: '/contracts/stats',
    expiring: '/contracts/expiring',
    export: '/contracts/export',
    templates: '/contracts/templates',
    template: (id: string) => `/contracts/templates/${id}`,
    fromTemplate: (templateId: string) => `/contracts/from-template/${templateId}`,
    guestLink: (id: string) => `/contracts/${id}/sign/guest-link`,
    milestoneInvoice: (id: string, milestoneId: string) => `/contracts/${id}/milestones/${milestoneId}/generate-invoice`,
    fullInvoice: (id: string) => `/contracts/${id}/generate-invoice`,
    contractInvoices: (id: string) => `/contracts/${id}/invoices`,
    // Public endpoints (no auth)
    publicValidate: (id: string) => `/p/contracts/${id}/sign/validate`,
    publicSign: (id: string) => `/p/contracts/${id}/sign`,
};

// Type guard for error with response
function isErrorWithResponse(error: unknown): error is { response?: { status?: number; data?: { message?: string } } } {
    return typeof error === 'object' && error !== null && 'response' in error;
}

// Contracts Service
export class ContractsService {
    // ==================== CONTRACT CRUD ====================

    async getAll(filters?: {
        searchQuery?: string;
        status?: string;
        startDateFrom?: string;
        startDateTo?: string;
        endDateFrom?: string;
        endDateTo?: string;
    }): Promise<Contract[]> {
        try {
            const params = new URLSearchParams();
            if (filters?.searchQuery) params.append('q', filters.searchQuery);
            if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
            if (filters?.startDateFrom) params.append('startDateFrom', filters.startDateFrom);
            if (filters?.startDateTo) params.append('startDateTo', filters.startDateTo);
            if (filters?.endDateFrom) params.append('endDateFrom', filters.endDateFrom);
            if (filters?.endDateTo) params.append('endDateTo', filters.endDateTo);

            const queryString = params.toString();
            const url = queryString ? `${API_ENDPOINTS.contracts}?${queryString}` : API_ENDPOINTS.contracts;

            const response = await api.get<ApiResponse<Contract[]>>(url);

            if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch contracts');

            return response.data.data.map(this.transformContract);
        } catch (error) {
            console.error('Failed to fetch contracts:', error);
            throw new Error('Failed to fetch contracts. Please try again.');
        }
    }

    async getById(id: string): Promise<Contract | null> {
        try {
            const response = await api.get<ApiResponse<Contract>>(API_ENDPOINTS.contract(id));
            if (!response.data.success) throw new Error(response.data.message || 'Contract not found');
            return this.transformContract(response.data.data);
        } catch (error) {
            console.error('Failed to fetch contract:', error);
            if (isErrorWithResponse(error) && error.response?.status === 404) return null;
            throw new Error('Failed to fetch contract. Please try again.');
        }
    }

    async create(data: CreateContractData): Promise<Contract> {
        try {
            const response = await api.post<ApiResponse<Contract>>(API_ENDPOINTS.contracts, data);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to create contract');

            toast.success('Contract created successfully');
            return this.transformContract(response.data.data);
        } catch (error) {
            console.error('Failed to create contract:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to create contract. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async createFromTemplate(templateId: string, data: {
        partnershipId: string;
        title: string;
        totalValue: number;
        startDate: string;
        endDate: string;
    }): Promise<Contract> {
        try {
            const response = await api.post<ApiResponse<Contract>>(API_ENDPOINTS.fromTemplate(templateId), data);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to create contract from template');

            toast.success('Contract created from template successfully');
            return this.transformContract(response.data.data);
        } catch (error) {
            console.error('Failed to create contract from template:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to create contract from template. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async update(id: string, data: UpdateContractData): Promise<Contract> {
        try {
            const response = await api.patch<ApiResponse<Contract>>(API_ENDPOINTS.contract(id), data);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to update contract');

            toast.success('Contract updated successfully');
            return this.transformContract(response.data.data);
        } catch (error) {
            console.error('Failed to update contract:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to update contract. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async updateStatus(id: string, status: Contract['status']): Promise<Contract> {
        try {
            const response = await api.patch<ApiResponse<Contract>>(API_ENDPOINTS.status(id), { status });
            if (!response.data.success) throw new Error(response.data.message || 'Failed to update contract status');

            toast.success('Contract status updated successfully');
            return this.transformContract(response.data.data);
        } catch (error) {
            console.error('Failed to update contract status:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to update contract status. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async delete(id: string): Promise<void> {
        try {
            const response = await api.delete<ApiResponse<void>>(API_ENDPOINTS.contract(id));
            if (!response.data.success) throw new Error(response.data.message || 'Failed to delete contract');

            toast.success('Contract deleted successfully');
        } catch (error) {
            console.error('Failed to delete contract:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to delete contract. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    // ==================== SIGNATURES ====================

    async signByCreator(id: string, signature?: string): Promise<Contract> {
        try {
            const response = await api.post<ApiResponse<Contract>>(API_ENDPOINTS.signCreator(id), { signature });
            if (!response.data.success) throw new Error(response.data.message || 'Failed to sign contract');

            toast.success('Contract signed by creator successfully');
            return this.transformContract(response.data.data);
        } catch (error) {
            console.error('Failed to sign contract:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to sign contract. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async signByBrand(id: string, signature?: string): Promise<Contract> {
        try {
            const response = await api.post<ApiResponse<Contract>>(API_ENDPOINTS.signBrand(id), { signature });
            if (!response.data.success) throw new Error(response.data.message || 'Failed to sign contract');

            toast.success('Contract signed by brand successfully');
            return this.transformContract(response.data.data);
        } catch (error) {
            console.error('Failed to sign contract:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to sign contract. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    // ==================== GUEST ACCESS (CLIENT SIGNING) ====================

    async generateGuestLink(id: string): Promise<{ link: string; expiresAt: string }> {
        try {
            const response = await api.post<ApiResponse<{ link: string; expiresAt: string }>>(API_ENDPOINTS.guestLink(id));
            if (!response.data.success) throw new Error(response.data.message || 'Failed to generate guest link');

            toast.success('Guest signing link generated!');
            return response.data.data;
        } catch (error) {
            console.error('Failed to generate guest link:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to generate guest link. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async validateGuestToken(id: string, token: string): Promise<Contract> {
        try {
            const response = await api.post<ApiResponse<Contract>>(API_ENDPOINTS.publicValidate(id), { token });
            if (!response.data.success) throw new Error(response.data.message || 'Invalid or expired link');

            return this.transformContract(response.data.data);
        } catch (error) {
            console.error('Failed to validate guest token:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Invalid or expired link. Please request a new signing link.';
            throw new Error(message);
        }
    }

    async signByGuest(id: string, data: { token: string; signature: string; name: string; title?: string }): Promise<Contract> {
        try {
            const response = await api.post<ApiResponse<Contract>>(API_ENDPOINTS.publicSign(id), data);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to sign contract');

            toast.success('Contract signed successfully!');
            return this.transformContract(response.data.data);
        } catch (error) {
            console.error('Failed to sign contract:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to sign contract. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    // ==================== DELIVERABLES ====================

    async addDeliverable(id: string, deliverable: { title: string; description?: string; deadline?: string }): Promise<Contract> {
        try {
            const response = await api.post<ApiResponse<Contract>>(API_ENDPOINTS.deliverables(id), deliverable);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to add deliverable');

            toast.success('Deliverable added successfully');
            return this.transformContract(response.data.data);
        } catch (error) {
            console.error('Failed to add deliverable:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to add deliverable. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async updateDeliverable(
        id: string,
        deliverableId: string,
        data: { title?: string; description?: string; deadline?: string; status?: string }
    ): Promise<Contract> {
        try {
            const response = await api.patch<ApiResponse<Contract>>(API_ENDPOINTS.deliverable(id, deliverableId), data);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to update deliverable');

            toast.success('Deliverable updated successfully');
            return this.transformContract(response.data.data);
        } catch (error) {
            console.error('Failed to update deliverable:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to update deliverable. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    // ==================== AMENDMENTS ====================

    async addAmendment(id: string, changes: string): Promise<Contract> {
        try {
            const response = await api.post<ApiResponse<Contract>>(API_ENDPOINTS.amendments(id), { changes });
            if (!response.data.success) throw new Error(response.data.message || 'Failed to add amendment');

            toast.success('Amendment added successfully');
            return this.transformContract(response.data.data);
        } catch (error) {
            console.error('Failed to add amendment:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to add amendment. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    // ==================== FLAGS ====================

    async addFlag(id: string, flag: { type: ContractFlag['type']; description: string }): Promise<Contract> {
        try {
            const response = await api.post<ApiResponse<Contract>>(API_ENDPOINTS.flags(id), flag);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to add flag');

            toast.success('Issue flagged successfully');
            return this.transformContract(response.data.data);
        } catch (error) {
            console.error('Failed to add flag:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to flag issue. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async resolveFlag(id: string, flagId: string, resolution: string): Promise<Contract> {
        try {
            const response = await api.patch<ApiResponse<Contract>>(API_ENDPOINTS.resolveFlag(id, flagId), { resolution });
            if (!response.data.success) throw new Error(response.data.message || 'Failed to resolve flag');

            toast.success('Flag resolved successfully');
            return this.transformContract(response.data.data);
        } catch (error) {
            console.error('Failed to resolve flag:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to resolve flag. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    // ==================== INVOICE GENERATION ====================

    async generateInvoiceForMilestone(contractId: string, milestoneId: string, dueDate?: Date): Promise<{
        invoice: any;
        contract: Contract;
    }> {
        try {
            const response = await api.post<ApiResponse<{ invoice: any; contract: Contract }>>(
                API_ENDPOINTS.milestoneInvoice(contractId, milestoneId),
                { dueDate: dueDate?.toISOString() }
            );
            if (!response.data.success) throw new Error(response.data.message || 'Failed to generate invoice');

            toast.success('Invoice generated successfully!');
            return {
                invoice: response.data.data.invoice,
                contract: this.transformContract(response.data.data.contract),
            };
        } catch (error) {
            console.error('Failed to generate invoice for milestone:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to generate invoice. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async generateFullContractInvoice(contractId: string, dueDate?: Date): Promise<{
        invoice: any;
        contract: Contract;
    }> {
        try {
            const response = await api.post<ApiResponse<{ invoice: any; contract: Contract }>>(
                API_ENDPOINTS.fullInvoice(contractId),
                { dueDate: dueDate?.toISOString() }
            );
            if (!response.data.success) throw new Error(response.data.message || 'Failed to generate invoice');

            toast.success('Invoice generated successfully!');
            return {
                invoice: response.data.data.invoice,
                contract: this.transformContract(response.data.data.contract),
            };
        } catch (error) {
            console.error('Failed to generate full contract invoice:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to generate invoice. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async getContractInvoices(contractId: string): Promise<any[]> {
        try {
            const response = await api.get<ApiResponse<any[]>>(API_ENDPOINTS.contractInvoices(contractId));
            if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch invoices');
            return response.data.data;
        } catch (error) {
            console.error('Failed to fetch contract invoices:', error);
            return [];
        }
    }

    // ==================== FILE UPLOAD ====================

    async uploadDocument(id: string, file: File): Promise<Contract> {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await api.upload<ApiResponse<Contract>>(API_ENDPOINTS.upload(id), formData);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to upload document');

            toast.success('Document uploaded successfully');
            return this.transformContract(response.data.data);
        } catch (error) {
            console.error('Failed to upload document:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to upload document. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    // ==================== STATISTICS ====================

    async getStats(): Promise<ContractStats> {
        try {
            const response = await api.get<ApiResponse<ContractStats>>(API_ENDPOINTS.stats);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch statistics');
            return response.data.data;
        } catch (error) {
            console.error('Failed to fetch contract statistics:', error);
            throw new Error('Failed to fetch contract statistics. Please try again.');
        }
    }

    async getExpiring(days: number = 30): Promise<Contract[]> {
        try {
            const response = await api.get<ApiResponse<Contract[]>>(`${API_ENDPOINTS.expiring}?days=${days}`);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch expiring contracts');
            return response.data.data.map(this.transformContract);
        } catch (error) {
            console.error('Failed to fetch expiring contracts:', error);
            throw new Error('Failed to fetch expiring contracts. Please try again.');
        }
    }

    // ==================== EXPORT ====================

    async export(format: 'csv' | 'json' = 'json'): Promise<Blob> {
        try {
            const response = await api.get(`${API_ENDPOINTS.export}?format=${format}`, { responseType: 'blob' });
            return response.data;
        } catch (error) {
            console.error('Failed to export contracts:', error);
            throw new Error('Failed to export contracts. Please try again.');
        }
    }

    // ==================== TEMPLATES ====================

    async getTemplates(category?: string): Promise<ContractTemplate[]> {
        try {
            const params = category && category !== 'all' ? `?category=${category}` : '';
            const response = await api.get<ApiResponse<ContractTemplate[]>>(`${API_ENDPOINTS.templates}${params}`);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch templates');
            return response.data.data.map(this.transformTemplate);
        } catch (error) {
            console.error('Failed to fetch templates:', error);
            throw new Error('Failed to fetch templates. Please try again.');
        }
    }

    async getTemplateById(id: string): Promise<ContractTemplate | null> {
        try {
            const response = await api.get<ApiResponse<ContractTemplate>>(API_ENDPOINTS.template(id));
            if (!response.data.success) throw new Error(response.data.message || 'Template not found');
            return this.transformTemplate(response.data.data);
        } catch (error) {
            console.error('Failed to fetch template:', error);
            if (isErrorWithResponse(error) && error.response?.status === 404) return null;
            throw new Error('Failed to fetch template. Please try again.');
        }
    }

    async createTemplate(data: Partial<ContractTemplate>): Promise<ContractTemplate> {
        try {
            const response = await api.post<ApiResponse<ContractTemplate>>(API_ENDPOINTS.templates, data);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to create template');

            toast.success('Template created successfully');
            return this.transformTemplate(response.data.data);
        } catch (error) {
            console.error('Failed to create template:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to create template. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async updateTemplate(id: string, data: Partial<ContractTemplate>): Promise<ContractTemplate> {
        try {
            const response = await api.patch<ApiResponse<ContractTemplate>>(API_ENDPOINTS.template(id), data);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to update template');

            toast.success('Template updated successfully');
            return this.transformTemplate(response.data.data);
        } catch (error) {
            console.error('Failed to update template:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to update template. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async deleteTemplate(id: string): Promise<void> {
        try {
            const response = await api.delete<ApiResponse<void>>(API_ENDPOINTS.template(id));
            if (!response.data.success) throw new Error(response.data.message || 'Failed to delete template');

            toast.success('Template deleted successfully');
        } catch (error) {
            console.error('Failed to delete template:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to delete template. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    // ==================== HELPERS ====================

    private transformContract(contract: any): Contract {
        // Handle populated partnershipId - when populated, it becomes an object with brand, contact, email fields
        let partnership = contract.partnership;
        let partnershipId = contract.partnershipId;

        // If partnershipId is an object (populated), extract partnership data
        if (partnershipId && typeof partnershipId === 'object') {
            const populatedPartnership = partnershipId as any;
            partnership = {
                id: populatedPartnership.id || populatedPartnership._id,
                brand: populatedPartnership.brand || '',
                contact: populatedPartnership.contact || '',
                email: populatedPartnership.email || '',
                value: populatedPartnership.value,
                contentType: populatedPartnership.contentType,
            };
            partnershipId = populatedPartnership.id || populatedPartnership._id;
        }

        return {
            ...contract,
            id: contract.id || contract._id,
            partnershipId: String(partnershipId || ''),
            partnership,
            createdAt: contract.createdAt || new Date().toISOString(),
            updatedAt: contract.updatedAt || new Date().toISOString(),
            deliverables: Array.isArray(contract.deliverables) ? contract.deliverables.map((d: any) => ({
                ...d,
                id: d.id || d._id,
            })) : [],
            amendments: Array.isArray(contract.amendments) ? contract.amendments.map((a: any) => ({
                ...a,
                id: a.id || a._id,
            })) : [],
            flags: Array.isArray(contract.flags) ? contract.flags.map((f: any) => ({
                ...f,
                id: f.id || f._id,
            })) : [],
            tags: Array.isArray(contract.tags) ? contract.tags : [],
            status: contract.status || 'draft',
            autoRenewal: contract.autoRenewal || false,
            terminationNoticeDays: contract.terminationNoticeDays || 30,
            totalValue: Number(contract.totalValue) || 0,
        };
    }

    private transformTemplate(template: any): ContractTemplate {
        return {
            ...template,
            id: template.id || template._id,
            createdAt: template.createdAt || new Date().toISOString(),
            updatedAt: template.updatedAt || new Date().toISOString(),
            isActive: template.isActive !== false,
            category: template.category || 'sponsorship',
        };
    }
}

// Singleton instance
export const contractsService = new ContractsService();
